import { useEffect, useState } from 'react';
import {
  useApi,
  identityApiRef,
  configApiRef,
} from '@backstage/core-plugin-api';
import {
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import RefreshIcon from '@material-ui/icons/Refresh';
import GetAppIcon from '@material-ui/icons/GetApp';
import { InfoCard, Progress } from '@backstage/core-components';
import { spassuCodeApiRef } from '../../api';
import { WorkerStatusIndicator } from '../WorkerStatusIndicator';
import workerDesktopScreenshot from '../../assets/worker-desktop.png';
import { resolveWorkerDownloadLink } from './workerDownloadLink';

const useStyles = makeStyles(theme => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  tokenField: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
  },
  copyButton: {
    padding: theme.spacing(0.5),
  },
  instructions: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  screenshot: {
    display: 'block',
    maxWidth: '100%',
    width: 560,
    marginTop: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  downloadRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
}));

export const WorkerTokenSettingsTab = () => {
  const classes = useStyles();
  const api = useApi(spassuCodeApiRef);
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);

  const [token, setToken] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [tokenTtlSeconds, setTokenTtlSeconds] = useState<number | null>(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState<'token' | 'userId' | null>(null);

  const workerDownloadLink = resolveWorkerDownloadLink(
    configApi.getOptionalString('spassuCode.worker.link'),
  );

  // Buscar userId ao montar para preencher o comando local mesmo sem token gerado.
  useEffect(() => {
    identityApi
      .getBackstageIdentity()
      .then(identity => {
        setUserId(identity.userEntityRef);
      })
      .catch(() => {});
  }, [identityApi]);

  const generateToken = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getWorkerToken();
      setToken(result.token);
      setUserId(result.userId);
      setTokenTtlSeconds(result.ttlSeconds);
      setTokenExpiresAt(result.expiresAt);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao gerar token');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string, field: 'token' | 'userId') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(field);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // fallback
    }
  };

  const maskedToken = token
    ? `${token.substring(0, 8)}${'*'.repeat(
        Math.max(0, token.length - 12),
      )}${token.substring(token.length - 4)}`
    : '';

  let tokenHelperText = 'Token gerado. Renove quando expirar.';
  if (copied === 'token') {
    tokenHelperText = 'Copiado!';
  } else if (tokenTtlSeconds && tokenExpiresAt) {
    tokenHelperText = `Token expira em ${Math.floor(
      tokenTtlSeconds / 3600,
    )}h (${new Date(tokenExpiresAt).toLocaleString('pt-BR')}).`;
  }

  return (
    <Grid container direction="column" spacing={3}>
      <Grid item>
        <InfoCard
          title="Worker Token"
          subheader="Gere um token para autenticar seu worker local"
        >
          <div className={classes.statusRow}>
            <Typography variant="body2">Status:</Typography>
            <WorkerStatusIndicator />
          </div>

          {loading && <Progress />}

          {error && (
            <Typography color="error" variant="body2" className={classes.field}>
              {error}
            </Typography>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={generateToken}
            disabled={loading}
            startIcon={<RefreshIcon />}
          >
            {token ? 'Gerar Novo Token' : 'Gerar Token'}
          </Button>

          {token && userId && (
            <Grid container spacing={2} style={{ marginTop: 16 }}>
              <Grid item xs={12}>
                <TextField
                  label="User ID"
                  value={userId}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{
                    readOnly: true,
                    className: classes.tokenField,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          className={classes.copyButton}
                          onClick={() => copyToClipboard(userId, 'userId')}
                          title="Copiar"
                          size="small"
                        >
                          <FileCopyOutlinedIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  helperText={
                    copied === 'userId'
                      ? 'Copiado!'
                      : 'Seu identificador no Backstage'
                  }
                  className={classes.field}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Token"
                  value={showToken ? token : maskedToken}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{
                    readOnly: true,
                    className: classes.tokenField,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          className={classes.copyButton}
                          onClick={() => setShowToken(!showToken)}
                          title={showToken ? 'Ocultar' : 'Mostrar'}
                          size="small"
                        >
                          {showToken ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                        <IconButton
                          className={classes.copyButton}
                          onClick={() => copyToClipboard(token, 'token')}
                          title="Copiar"
                          size="small"
                        >
                          <FileCopyOutlinedIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  helperText={tokenHelperText}
                  className={classes.field}
                />
              </Grid>
            </Grid>
          )}
        </InfoCard>
      </Grid>

      <Grid item>
        <InfoCard
          title="Como usar"
          subheader="Instale o Worker Desktop e conecte com o token gerado acima"
        >
          <div className={classes.downloadRow}>
            <Typography variant="body2">
              1. Baixe e instale o aplicativo Worker Desktop:
            </Typography>
            {workerDownloadLink ? (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<GetAppIcon />}
                href={workerDownloadLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Baixar Worker Desktop
              </Button>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Link de download não configurado.
              </Typography>
            )}
          </div>

          <Typography variant="body2">
            2. Abra o aplicativo e preencha os campos conforme abaixo:
          </Typography>
          <img
            src={workerDesktopScreenshot}
            alt="Tela do Worker Desktop com os campos Servidor, Usuário, Modelo, Token e Diretório de trabalho"
            className={classes.screenshot}
          />

          <div className={classes.instructions}>
            <Typography variant="subtitle2" gutterBottom>
              Preenchimento dos campos:
            </Typography>
            <Typography variant="body2" component="ul">
              <li>
                <strong>Servidor</strong>: selecione o ambiente do Spassu Code
                ao qual deseja conectar (ex.: Spassu Code).
              </li>
              <li>
                <strong>Usuário</strong>: seu identificador no Backstage
                {userId ? (
                  <>
                    {' '}
                    — <code>{userId}</code>
                  </>
                ) : (
                  <> (exibido acima após gerar o token)</>
                )}
                .
              </li>
              <li>
                <strong>Modelo</strong> (um por engine): escolha o modelo que
                este worker deve usar em cada engine. A seleção vale nas
                execuções iniciadas pelo Backstage; o engine de cada atividade
                continua sendo definido aqui, na criação da atividade.
              </li>
              <li>
                <strong>Token</strong>: cole o token gerado nesta página (botão
                "Gerar Token" acima). Renove aqui quando expirar.
              </li>
              <li>
                <strong>Diretório de trabalho</strong>: pasta local onde o
                worker clona os repositórios das atividades. Pode manter a
                sugerida.
              </li>
            </Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              3. Clique em <strong>Iniciar Worker</strong>. Quando o aplicativo
              mostrar o estado ativo, o indicador de status no topo desta página
              passa a Conectado.
            </Typography>
          </div>
        </InfoCard>
      </Grid>
    </Grid>
  );
};
